import { ReactNode } from 'react'

import { EditorOptions } from '@tiptap/core'
import { Slice } from '@tiptap/pm/model'
import { EditorView } from '@tiptap/pm/view'
import { Editor, Range } from '@tiptap/react'

import {
  Embed,
  Member,
  Poll,
  PollEntityType,
  PollParentHintInput,
  File as TribeFile,
} from '@tribeplatform/gql-client/types'

import type { ImageAttributes } from '../extensions/image/index.js'
import type { MenuItem } from '../extensions/slash-menu/items.js'

export interface ComposerRefImperativeHandle {
  clear: () => void
  focus: () => void
  getEditorHTML: () => string | null
  getEditor: () => Editor
  isEmpty: () => boolean
  isTouched: () => boolean
}

export type HandleAttachmentSelect = (files: File[]) => Promise<void>

export type ComposerProviderType = {
  content?: string
  /**
   * @deprecated
   */
  mentions?: Partial<Member>[]
  /**
   * @deprecated
   */
  embeds?: Embed[]
  /**
   * @deprecated
   */
  attachments?: TribeFile[]
  editorOptions?: Partial<EditorOptions>
  /** to be used as a cached data for the search function on the mention module */
  cachedMentions?: Mention[]
  placeholder?: string
  extensionConfig?: ComposerExtensionConfig
  pasteHandler?: (view: EditorView, event: ClipboardEvent, slice: Slice) => void
  children?: ReactNode
  poll?: {
    canCreate?: boolean
    entityTypeId: Poll['parentEntityId']
    entityType: PollEntityType
    parentHint?: PollParentHintInput
  }
}

export type DialogKinds =
  | 'image'
  | 'video'
  | 'audio'
  | 'link'
  | 'embed'
  | 'giphy'
  | 'textFormatMenu'
  | 'poll'

export type DialogsState = {
  audio: boolean
  video: boolean
  embed: boolean
  image: boolean
  link: boolean
  giphy: boolean
  embedItem: MenuItem | undefined
  textFormatMenu: boolean
}

type State = DialogsState

export type ComposerContextType = {
  /** To check whether the context is wrapped within the ComposerProvider or not */
  withProvider: boolean
  isTouched: boolean
  editor: Editor
  state: State
  toggleDialog: (key: DialogKinds, value: boolean) => void
  onFileUpload: (
    files: FileList | Array<File>,
    options?: {
      asAttachment?: boolean
      attrs?: Partial<ImageAttributes>
    },
  ) => Promise<void>
  onSlashMenuItemClick: (args: {
    editor: Editor
    props: MenuItem
    range?: Range
  }) => void
  poll?: ComposerProviderType['poll']
}

export type ComposerConfigContextType = {
  extensions: ComposerExtensionConfig
}

export type Attachment = TribeFile & {
  /** For showing the loading spinner */
  isLoading?: boolean
}
export enum ReducerActionKind {
  SET = 'SET',
  SET_EMBED_ITEM = 'SET_EMBED_ITEM',
}

export interface DialogsAction {
  type: ReducerActionKind
  key?: DialogKinds
  embedItem?: MenuItem
  value?: boolean
}

export enum ComposerModuleName {
  BulletList = 'bulletList',
  NumberedList = 'numberedList',
  LargeHeader = 'largeHeader',
  MediumHeader = 'mediumHeader',
  Image = 'image',
  Attachment = 'attachment',
  Emoji = 'emoji',
  Mention = 'mention',
  Embed = 'embed',
  Code = 'code',
}
export type ComposerExtensionConfig = {
  heading: boolean
  mention: boolean
  emoji: boolean
  embed: boolean
  attachment: boolean
  image: boolean
  video: boolean
  code: boolean
  list: boolean
  table: boolean
  tableOfContents: boolean
  formatting: {
    bold: boolean
    italic: boolean
    underline: boolean
    strikethrough: boolean
  }
  poll: boolean
}

export interface ComposerEmbedProps {
  embed?: Embed
  isReadOnly: boolean
  handleEmbedPaste?: (value: string) => Embed
  handleEmbedInvalid?: () => void
  placeholder?: string
}

export type AttachmentsProps = {
  attachments?: Attachment[]
  isReadOnly?: boolean
  onAttachmentDelete?: (attachment: Attachment) => void
}

export type Mention = {
  id: string
  title: string
  icon: string
}

export type FileWithID = File & {
  id?: string
}
